import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  FaComment,
  FaEllipsisH,
  FaEye,
  FaHeart,
  FaMapMarkerAlt,
  FaPause,
  FaPlay,
  FaPlus,
  FaRegHeart,
  FaTrash,
  FaVolumeMute,
  FaVolumeUp
} from 'react-icons/fa';

import '../css/MomentsFeed.css';

function MomentsFeed() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const observerRef = useRef(null);
  const momentRefs = useRef(new Map());
  const registeredViewsRef = useRef(new Set());

  const [moments, setMoments] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [activeMomentId, setActiveMomentId] = useState(null);
  const [mutedMomentIds, setMutedMomentIds] = useState(new Set());
  const [pausedMomentIds, setPausedMomentIds] = useState(new Set());
  const [openMenuId, setOpenMenuId] = useState(null);

  const token = localStorage.getItem('authToken');

  const authorizationHeader = useMemo(() => {
    if (!token) return null;

    return token.startsWith('Bearer ')
      ? token
      : `Bearer ${token}`;
  }, [token]);

  const api = useMemo(() => {
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: authorizationHeader
        ? {
            Authorization: authorizationHeader
          }
        : {}
    });
  }, [authorizationHeader]);

  const fetchMoments = useCallback(
    async (pageNumber = 0, append = false) => {
      if (!authorizationHeader) {
        setErrorMessage(
          t(
            'moments.viewAuthenticationRequired',
            'Vous devez être connecté pour voir les Moments.'
          )
        );
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      setErrorMessage('');

      try {
        const response = await api.get('/api/moments/feed', {
          params: {
            page: pageNumber,
            size: 10
          }
        });

        const payload = response.data;
        const content = Array.isArray(payload?.content)
          ? payload.content
          : Array.isArray(payload)
          ? payload
          : [];

        setMoments((previous) => {
          if (!append) return content;

          const existingIds = new Set(
            previous.map((moment) => moment.id)
          );

          return [
            ...previous,
            ...content.filter(
              (moment) => !existingIds.has(moment.id)
            )
          ];
        });

        setPage(pageNumber);

        if (typeof payload?.last === 'boolean') {
          setHasMore(!payload.last);
        } else {
          setHasMore(content.length === 10);
        }
      } catch (error) {
        console.error('❌ Moments feed error:', error);

        setErrorMessage(
          error.response?.data?.message ||
            t(
              'moments.feedError',
              'Impossible de charger les Moments.'
            )
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [api, authorizationHeader, t]
  );

  useEffect(() => {
    document.body.classList.add('moments-feed-body');

    fetchMoments(0, false);

    const handleRefresh = () => {
      registeredViewsRef.current.clear();
      fetchMoments(0, false);
    };

    window.addEventListener(
      'refresh-moments',
      handleRefresh
    );

    return () => {
      document.body.classList.remove('moments-feed-body');

      window.removeEventListener(
        'refresh-moments',
        handleRefresh
      );
    };
  }, [fetchMoments]);

  const registerView = useCallback(
    async (momentId) => {
      if (
        !momentId ||
        registeredViewsRef.current.has(momentId)
      ) {
        return;
      }

      registeredViewsRef.current.add(momentId);

      try {
        const response = await api.post(
          `/api/moments/${momentId}/view`
        );

        const viewsCount = response.data?.viewsCount;

        if (typeof viewsCount === 'number') {
          setMoments((previous) =>
            previous.map((moment) =>
              moment.id === momentId
                ? {
                    ...moment,
                    viewsCount
                  }
                : moment
            )
          );
        }
      } catch (error) {
        registeredViewsRef.current.delete(momentId);

        console.error(
          '❌ Register Moment view failed:',
          error
        );
      }
    },
    [api]
  );

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const momentId = Number(
            entry.target.dataset.momentId
          );

          const video =
            entry.target.querySelector('video');

          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.65
          ) {
            setActiveMomentId(momentId);
            registerView(momentId);

            if (
              video &&
              !pausedMomentIds.has(momentId)
            ) {
              video.play().catch(() => {});
            }
          } else if (video) {
            video.pause();
          }
        });
      },
      {
        threshold: [0.25, 0.65, 0.9]
      }
    );

    momentRefs.current.forEach((element) => {
      if (element) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [moments, pausedMomentIds, registerView]);

  const setMomentRef = (momentId, element) => {
    if (element) {
      momentRefs.current.set(momentId, element);
    } else {
      momentRefs.current.delete(momentId);
    }
  };

  const updateMoment = (momentId, changes) => {
    setMoments((previous) =>
      previous.map((moment) =>
        moment.id === momentId
          ? {
              ...moment,
              ...changes
            }
          : moment
      )
    );
  };

  const handleLike = async (moment) => {
    const previousLiked = Boolean(
      moment.likedByCurrentUser
    );

    const previousCount =
      Number(moment.likesCount) || 0;

    updateMoment(moment.id, {
      likedByCurrentUser: !previousLiked,
      likesCount: previousLiked
        ? Math.max(0, previousCount - 1)
        : previousCount + 1
    });

    try {
      const response = await api.post(
        `/api/moments/${moment.id}/like`
      );

      updateMoment(moment.id, {
        likedByCurrentUser:
          response.data?.liked ?? !previousLiked,
        likesCount:
          response.data?.likesCount ??
          previousCount
      });
    } catch (error) {
      console.error('❌ Moment like failed:', error);

      updateMoment(moment.id, {
        likedByCurrentUser: previousLiked,
        likesCount: previousCount
      });
    }
  };

  const handleDelete = async (momentId) => {
    const confirmed = window.confirm(
      t(
        'moments.deleteConfirm',
        'Supprimer définitivement ce Moment ?'
      )
    );

    if (!confirmed) return;

    try {
      await api.delete(`/api/moments/${momentId}`);

      setMoments((previous) =>
        previous.filter(
          (moment) => moment.id !== momentId
        )
      );

      setOpenMenuId(null);
    } catch (error) {
      console.error(
        '❌ Delete Moment failed:',
        error
      );

      window.alert(
        error.response?.data?.message ||
          t(
            'moments.deleteError',
            'Impossible de supprimer ce Moment.'
          )
      );
    }
  };

  const toggleMuted = (momentId) => {
    setMutedMomentIds((previous) => {
      const next = new Set(previous);

      if (next.has(momentId)) {
        next.delete(momentId);
      } else {
        next.add(momentId);
      }

      return next;
    });
  };

  const togglePause = (momentId) => {
    setPausedMomentIds((previous) => {
      const next = new Set(previous);
      const element = momentRefs.current.get(momentId);
      const video = element?.querySelector('video');

      if (next.has(momentId)) {
        next.delete(momentId);
        video?.play().catch(() => {});
      } else {
        next.add(momentId);
        video?.pause();
      }

      return next;
    });
  };

  const getProfilePhoto = (moment) => {
    return (
      moment.userProfilePhoto ||
      '/default-profile.png'
    );
  };

  const getLocation = (moment) => {
    return [moment.city, moment.state, moment.country]
      .filter(Boolean)
      .join(', ');
  };

  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    const differenceSeconds = Math.max(
      1,
      Math.floor((Date.now() - date.getTime()) / 1000)
    );

    if (differenceSeconds < 60) {
      return t('moments.justNow', 'À l’instant');
    }

    const minutes = Math.floor(
      differenceSeconds / 60
    );

    if (minutes < 60) {
      return t('moments.minutesAgo', {
        defaultValue: 'Il y a {{count}} min',
        count: minutes
      });
    }

    const hours = Math.floor(minutes / 60);

    if (hours < 24) {
      return t('moments.hoursAgo', {
        defaultValue: 'Il y a {{count}} h',
        count: hours
      });
    }

    const days = Math.floor(hours / 24);

    return t('moments.daysAgo', {
      defaultValue: 'Il y a {{count}} j',
      count: days
    });
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchMoments(page + 1, true);
    }
  };

  if (loading) {
    return (
      <main className="moments-feed-page">
        <div className="moments-feed-loading">
          <div />
          <p>
            {t(
              'moments.loading',
              'Chargement des Moments...'
            )}
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="moments-feed-page">
      <section className="moments-feed-shell">
        {errorMessage && (
          <div
            className="moments-feed-error"
            role="alert"
          >
            <p>{errorMessage}</p>

            <button
              type="button"
              onClick={() => fetchMoments(0, false)}
            >
              {t(
                'common.retry',
                'Réessayer'
              )}
            </button>
          </div>
        )}

        {!errorMessage && moments.length === 0 && (
          <div className="moments-feed-empty">
            <div>
              <FaPlus />
            </div>

            <h2>
              {t(
                'moments.emptyTitle',
                'Aucun Moment pour le moment'
              )}
            </h2>

            <p>
              {t(
                'moments.emptyText',
                'Soyez la première personne à partager une photo ou une vidéo.'
              )}
            </p>

            <button
              type="button"
              onClick={() =>
                navigate('/moments/create')
              }
            >
              {t(
                'moments.publishFirst',
                'Publier un Moment'
              )}
            </button>
          </div>
        )}

        <div className="moments-feed-list">
          {moments.map((moment) => {
            const location = getLocation(moment);
            const isVideo =
              moment.mediaType === 'VIDEO' ||
              moment.contentType?.startsWith('video/');

            const isMuted =
              mutedMomentIds.has(moment.id);

            const isPaused =
              pausedMomentIds.has(moment.id);

            return (
              <article
                key={moment.id}
                ref={(element) =>
                  setMomentRef(moment.id, element)
                }
                data-moment-id={moment.id}
                className={
                  activeMomentId === moment.id
                    ? 'moments-feed-card is-active'
                    : 'moments-feed-card'
                }
              >
                <header className="moments-feed-card-header">
                  <button
                    type="button"
                    className="moments-feed-user"
                    onClick={() =>
                      navigate(
                        `/moments/user/${moment.userId}`
                      )
                    }
                  >
                    <img
                      src={getProfilePhoto(moment)}
                      alt={moment.userName || 'User'}
                      onError={(event) => {
                        event.currentTarget.src =
                          '/default-profile.png';
                      }}
                    />

                    <span>
                      <strong>
                        {moment.userName ||
                          t(
                            'moments.user',
                            'Utilisateur'
                          )}
                      </strong>

                      <small>
                        {formatRelativeTime(
                          moment.createdAt
                        )}

                        {location && (
                          <>
                            <span>•</span>
                            <FaMapMarkerAlt />
                            {location}
                          </>
                        )}
                      </small>
                    </span>
                  </button>

                  <div className="moments-feed-menu-wrapper">
                    <button
                      type="button"
                      className="moments-feed-more"
                      onClick={() =>
                        setOpenMenuId((current) =>
                          current === moment.id
                            ? null
                            : moment.id
                        )
                      }
                      aria-label={t(
                        'common.more',
                        'Plus'
                      )}
                    >
                      <FaEllipsisH />
                    </button>

                    {openMenuId === moment.id && (
                      <div className="moments-feed-menu">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              `/moments/user/${moment.userId}`
                            )
                          }
                        >
                          {t(
                            'moments.viewProfileMoments',
                            'Voir ses Moments'
                          )}
                        </button>

                        {moment.ownedByCurrentUser && (
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() =>
                              handleDelete(moment.id)
                            }
                          >
                            <FaTrash />
                            {t(
                              'common.delete',
                              'Supprimer'
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </header>

                <div
                  className="moments-feed-media"
                  onDoubleClick={() =>
                    handleLike(moment)
                  }
                >
                  {isVideo ? (
                    <video
                      src={moment.mediaUrl}
                      poster={moment.thumbnailUrl || ''}
                      muted={isMuted}
                      loop
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={moment.mediaUrl}
                      alt={
                        moment.caption ||
                        t(
                          'moments.photo',
                          'Moment'
                        )
                      }
                    />
                  )}

                  {isVideo && (
                    <>
                      <button
                        type="button"
                        className="moments-feed-video-control moments-feed-video-control--play"
                        onClick={() =>
                          togglePause(moment.id)
                        }
                        aria-label={
                          isPaused
                            ? t(
                                'common.play',
                                'Lire'
                              )
                            : t(
                                'common.pause',
                                'Pause'
                              )
                        }
                      >
                        {isPaused ? (
                          <FaPlay />
                        ) : (
                          <FaPause />
                        )}
                      </button>

                      <button
                        type="button"
                        className="moments-feed-video-control moments-feed-video-control--sound"
                        onClick={() =>
                          toggleMuted(moment.id)
                        }
                        aria-label={
                          isMuted
                            ? t(
                                'common.unmute',
                                'Activer le son'
                              )
                            : t(
                                'common.mute',
                                'Couper le son'
                              )
                        }
                      >
                        {isMuted ? (
                          <FaVolumeMute />
                        ) : (
                          <FaVolumeUp />
                        )}
                      </button>
                    </>
                  )}

                  <div className="moments-feed-media-gradient" />
                </div>

                <div className="moments-feed-content">
                  <div className="moments-feed-actions">
                    <button
                      type="button"
                      className={
                        moment.likedByCurrentUser
                          ? 'moments-feed-action is-liked'
                          : 'moments-feed-action'
                      }
                      onClick={() =>
                        handleLike(moment)
                      }
                    >
                      {moment.likedByCurrentUser ? (
                        <FaHeart />
                      ) : (
                        <FaRegHeart />
                      )}

                      <span>
                        {Number(
                          moment.likesCount
                        ) || 0}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="moments-feed-action"
                      onClick={() =>
                        navigate(
                          `/moments/${moment.id}/comments`
                        )
                      }
                    >
                      <FaComment />

                      <span>
                        {Number(
                          moment.commentsCount
                        ) || 0}
                      </span>
                    </button>

                    <div className="moments-feed-action moments-feed-action--static">
                      <FaEye />

                      <span>
                        {Number(
                          moment.viewsCount
                        ) || 0}
                      </span>
                    </div>
                  </div>

                  {moment.caption && (
                    <p className="moments-feed-caption">
                      <strong>
                        {moment.userName}
                      </strong>{' '}
                      {moment.caption}
                    </p>
                  )}

                  {moment.commentsEnabled &&
                    Number(moment.commentsCount) > 0 && (
                      <button
                        type="button"
                        className="moments-feed-comments-link"
                        onClick={() =>
                          navigate(
                            `/moments/${moment.id}/comments`
                          )
                        }
                      >
                        {t(
                          'moments.viewComments',
                          'Voir les commentaires'
                        )}
                      </button>
                    )}
                </div>
              </article>
            );
          })}
        </div>

        {hasMore && moments.length > 0 && (
          <button
            type="button"
            className="moments-feed-load-more"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore
              ? t(
                  'moments.loadingMore',
                  'Chargement...'
                )
              : t(
                  'moments.loadMore',
                  'Voir plus de Moments'
                )}
          </button>
        )}
      </section>

      <button
        type="button"
        className="moments-feed-floating-create"
        onClick={() =>
          navigate('/moments/create')
        }
        aria-label={t(
          'moments.create',
          'Créer un Moment'
        )}
      >
        <FaPlus />
      </button>
    </main>
  );
}

export default MomentsFeed;